/* eslint-disable @typescript-eslint/no-explicit-any */
import './claim-entity.detail.overview.scss';
import './common.scss';
import './claim-diary.scss';
import {
  Box,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import {
  Claim,
  TargetAreaType,
  TaskPriority,
  TaskStatus,
  TaskType,
  useGetUsers,
  UserGroup,
} from '@converge/graphql';
import { getUserOptions } from '../../../domain/submission/underwriting-queue-grid';
import {
  useUpsertTaskMutation,
  useDeleteTaskMutation,
  useCloseClaimDetailsQuery,
} from '@converge/graphql';
import {
  ConvergeCheckbox,
  ConvergeDetailAccordion,
  ErrorPageLayout,
  NewDateField,
  NewFormTextField,
} from '../../../presentation';
import {
  useForm,
  FormProvider,
  useFormContext,
  Controller,
} from 'react-hook-form';

import AddAlertOutlined from '@mui/icons-material/AddAlertOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelOutlined from '@mui/icons-material/CancelOutlined';

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ClaimEntityPageContainer } from './claim-entity.page.container';
import moment from 'moment-timezone';

import { useTasksQuery } from '@converge/graphql';
import { useConvergeSnackBar } from '../../../contexts';
import { TaskTextField, TaskTable, PriorityDropdown } from './components';
import DiaryAssigneeDropdown from './components/assignee-dropdown-diary';
import {
  ValidateDateType,
  validateFutureDate,
  validatePastDate,
} from '../utils';

// StyledTableContainer moved to task-table.tsx component

export interface IClaimDiaryOutletProps {
  insuredUuid: string;
}

export interface IClaimDiaryProps {
  claimEntity: Claim;
  claimUuid: string;
  litigation?: boolean | null | undefined;
  isNotActive: boolean;
}

// Task status mappings between API enum and component states

// Convert API task status to completed checkbox state
const getTaskCompletedValue = (status: string): boolean => {
  return status === TaskStatus.Done;
};

// Convert API task status to N/A checkbox state
const getTaskNAValue = (status: string): boolean => {
  return status === TaskStatus.NotApplicable;
};

// Get assignee email from task
const getTaskAssignee = (task: any): string => {
  // Try multiple possible paths for the assignee data
  // Prioritize userId since that's what we save as ownerEmail
  const assignee =
    task?.user?.node?.userId ||
    task?.user?.node?.username ||
    task?.assignee ||
    '';
  return assignee;
};

// Format date from API to display format for date input field
const formatDueDate = (dueDate: string | null | undefined): string => {
  if (!dueDate || typeof dueDate !== 'string') return '';
  // Extract just the date part from ISO string without timezone conversion
  // This preserves the date as it was stored in the backend
  return dueDate.split('T')[0];
};

// Check if a due date is past today (overdue)
const isDateOverdue = (dueDate: string | null): boolean => {
  if (!dueDate) return false;
  const today = moment().format('YYYY-MM-DD');
  const formattedDueDate = formatDueDate(dueDate);
  return formattedDueDate < today;
};

// We'll fetch real users from the API instead of using hardcoded values

export const ClaimDiary = () => {
  const { claimEntity, claimUuid, litigation, isNotActive } =
    useOutletContext<IClaimDiaryProps>();
  const effectiveDate =
    claimEntity?.quote?.node?.policies?.edges[0]?.node.startDate;

  const ownerEmail = claimEntity.user?.username ?? '';

  if (!claimEntity) {
    return (
      <ErrorPageLayout
        message="No claim data found"
        messageId="insured.detail.page.claimEntityNotFound"
      />
    );
  }

  return (
    <ClaimDiaryInner
      data={claimEntity}
      claimUuid={claimUuid}
      litigation={litigation}
      effectiveDate={effectiveDate}
      isNotActive={isNotActive}
      ownerEmail={ownerEmail}
    />
  );
};

export const ClaimDiaryInner = ({
  data,
  claimUuid, // We'll use this now for fetching tasks
  litigation,
  effectiveDate,
  isNotActive,
  ownerEmail,
}: {
  data: Claim;
  claimUuid?: string;
  litigation?: boolean | null | undefined;
  effectiveDate?: string;
  isNotActive: boolean;
  ownerEmail?: string;
}) => {
  const { notify } = useConvergeSnackBar();

  const minDate = moment().toDate();

  // Create a master form to manage all task rows
  const masterFormMethods = useForm<Record<string, any>>({
    defaultValues: {},
  });

  // Track initialized tasks to prevent re-initialization
  const initializedTasksRef = React.useRef(new Set<string>());

  // Initialize task form fields
  const initializeTaskFormFields = useCallback(
    (task: any) => {
      const taskId = task.id;

      if (!initializedTasksRef.current.has(taskId)) {
        initializedTasksRef.current.add(taskId);

        // Set initial values for this task - keep all empty for new tasks
        masterFormMethods.setValue(
          `task-${taskId}`,
          task.name || task.task || ''
        );
        masterFormMethods.setValue(
          `assignee-${taskId}`,
          getTaskAssignee(task) || task.assignee || ''
        );
        masterFormMethods.setValue(`priority-${taskId}`, task.priority || '');
        masterFormMethods.setValue(`date-${taskId}`, task.dueDate || '');
        masterFormMethods.setValue(
          `completed-date-${taskId}`,
          task.completedDate || ''
        );
        masterFormMethods.setValue(`notes-${taskId}`, task.note || '');
        masterFormMethods.setValue(
          `completed-${taskId}`,
          task.status !== undefined ? getTaskCompletedValue(task.status) : false
        );
        masterFormMethods.setValue(
          `na-${taskId}`,
          task.status !== undefined ? getTaskNAValue(task.status) : false
        );
        // For additional tasks, also initialize the status field
        // Check if this is an additional task by checking the task.type or by task ID patterns
        const isAdditionalTask = task.type === TaskType.AdditionalTask;

        if (isAdditionalTask) {
          masterFormMethods.setValue(`status-${taskId}`, task.status || false);
        }
      }
    },
    [masterFormMethods]
  );

  // Fetch users from the Claims group to populate the assignee dropdown
  const { data: claimUsersList } = useGetUsers({
    userGroups: [UserGroup.Claims],
  });

  // Convert user data to options format for dropdown
  const assigneeOptions = useMemo(() => {
    return getUserOptions(claimUsersList, true) || [];
  }, [claimUsersList]);

  // Set up close claim details query for refetching
  const { refetch: refetchCloseClaimDetails } = useCloseClaimDetailsQuery({
    claimUuid: claimUuid || '',
  });

  // Initialize the upsert task mutation
  const { upsertTask } = useUpsertTaskMutation({
    onCompleted: () => {
      // Refetch close claim details when task is updated
      refetchCloseClaimDetails();
    },
    onError: (error: any) => {
      console.error('Failed to update task status:', error);
      notify(`Failed to update task: ${error.message || 'Unknown error'}`, {
        variant: 'error',
      });
    },
  });

  // Initialize the delete task mutation
  const { deleteTask } = useDeleteTaskMutation({
    onCompleted: () => {
      // Refetch close claim details when task is deleted
      refetchCloseClaimDetails();
    },
    onError: (error: any) => {
      console.error('Failed to delete task:', error);
      notify(`Failed to delete task: ${error.message || 'Unknown error'}`, {
        variant: 'error',
      });
    },
  });

  const handleUpdateDueDate = useCallback(
    async (taskId: string, date: Date | null) => {
      try {
        notify('Updating due date...', { variant: 'info' });
        await upsertTask({
          variables: {
            details: {
              taskUuid: taskId,
              targetArea: TargetAreaType.Claim,
              dueDate: date ? date.toISOString() : null,
            },
          },
        });

        notify('Due date updated successfully', {
          variant: 'success',
        });
      } catch (error) {
        console.error('Error updating task:', error);
        notify(
          `Failed to update task: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          { variant: 'error' }
        );
      }
    },
    [upsertTask, notify]
  );

  const handleUpdateCompletedDate = useCallback(
    async (taskId: string, date: Date | null, isCompleted: boolean) => {
      try {
        await upsertTask({
          variables: {
            details: {
              taskUuid: taskId,
              targetArea: TargetAreaType.Claim,
              completedDate: date ? date.toISOString() : null,
              status: isCompleted ? TaskStatus.Done : TaskStatus.ToDo,
            },
          },
        });

        notify(
          isCompleted
            ? 'Task completed successfully'
            : 'Task completion cleared',
          { variant: 'success' }
        );
      } catch (error) {
        console.error('Error updating task:', error);
        notify(
          `Failed to update task: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          { variant: 'error' }
        );
      }
    },
    [upsertTask, notify]
  );

  // Handle creating new task or updating an existing one
  const handleUpsertTask = useCallback(
    async (
      taskId: string,
      taskLabel: string,
      taskType: TaskType = TaskType.DefaultTask
    ): Promise<any> => {
      if (!taskLabel.trim()) {
        return null; // Don't create empty tasks
      }

      // Check if this is an update or create operation
      const isUpdate =
        taskId &&
        taskId !== 'additional-task-default' &&
        !taskId.startsWith('additional-task-');

      try {
        notify(isUpdate ? 'Updating task...' : 'Creating task...', {
          variant: 'info',
        });

        // Prepare mutation variables for either update or create
        const details = isUpdate
          ? {
              // Update existing task
              taskUuid: taskId,
              targetArea: TargetAreaType.Claim,
              label: taskLabel.trim(),
            }
          : {
              // Create a new task
              label: taskLabel.trim(),
              targetArea: TargetAreaType.Claim,
              targetUuid: claimUuid,
              type: taskType, // 'DEFAULT_TASK' for core tasks, 'ADDITIONAL_TASK' for additional tasks
              status: TaskStatus.ToDo,
              system: false,
              ownerEmail: ownerEmail,
            };

        // Execute the upsert operation
        const result = await upsertTask({
          variables: { details },
        });

        notify(
          isUpdate ? `Task updated successfully` : `Task created successfully`,
          {
            variant: 'success',
          }
        );

        // If this was a create operation (not update), clear the form fields
        if (!isUpdate) {
          // Clear all form fields for this task - use empty values for everything
          masterFormMethods.setValue(`task-${taskId}`, '');
          masterFormMethods.setValue(`assignee-${taskId}`, '');
          masterFormMethods.setValue(`priority-${taskId}`, '');
          masterFormMethods.setValue(`date-${taskId}`, '');
          masterFormMethods.setValue(`notes-${taskId}`, '');
          masterFormMethods.setValue(`completed-${taskId}`, false);
          masterFormMethods.setValue(`status-${taskId}`, false);
          masterFormMethods.setValue(`na-${taskId}`, false);

          // Remove from initialized tasks so it can be re-initialized with fresh values
          initializedTasksRef.current.delete(taskId);
        }

        return result;
      } catch (error) {
        console.error('Error creating task:', error);
        notify(
          `Failed to create task: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          {
            variant: 'error',
          }
        );
        return null;
      }
    },
    [notify, claimUuid, ownerEmail, upsertTask, masterFormMethods]
  );

  const [isCoreTasksOpen, setCoreTasksOpen] = useState(true);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [isAdditionalTasksOpen, setAdditionalTasksOpen] = useState(true);
  // We no longer need local task status state as we'll be using the API status directly
  const [openTextAreaId, setOpenTextAreaId] = useState<string | null>(null);
  const [additionalTasks, setAdditionalTasks] = useState<any[]>([]);

  // Task data state
  const [coreTasks, setCoreTasks] = useState<any[]>([]);

  const {
    loading: coreTasksLoading,
    error: coreTasksError,
    data: coreTasksData,
  } = useTasksQuery({
    pageNumber: 0,
    pageSize: 50,
    filters: {
      targetArea: TargetAreaType.Claim,
      targetUuid: claimUuid,
      // No type filter - will filter on frontend
    },
  });

  // Handle deleting a task
  const handleDeleteTask = useCallback(
    async (taskId: string) => {
      if (
        !taskId ||
        taskId === 'additional-task-default' ||
        taskId.startsWith('additional-task-')
      ) {
        // Just remove from local state for temporary tasks
        setAdditionalTasks((prev) => prev.filter((t) => t.id !== taskId));
        return;
      }

      try {
        notify('Deleting task...', {
          variant: 'info',
        });

        // Delete the task via API
        await deleteTask({
          variables: {
            taskUuid: taskId,
          },
        });

        notify('Task deleted successfully', {
          variant: 'success',
        });
      } catch (error) {
        console.error('Error deleting task:', error);
        notify(
          `Failed to delete task: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          {
            variant: 'error',
          }
        );
      }
    },
    [deleteTask, notify]
  );

  const handleUpdateTaskOwner = useCallback(
    async (event: SelectChangeEvent<unknown>, taskUuid: string) => {
      const userUuid = event?.target?.value as string;
      if (
        taskUuid === 'additional-task-default' ||
        taskUuid.startsWith('additional-task-')
      ) {
        return;
      }
      notify('Saving task owner...', {
        variant: 'info',
      });

      try {
        await upsertTask({
          variables: {
            details: {
              taskUuid: taskUuid,
              targetUuid: claimUuid,
              targetArea: TargetAreaType.Claim,
              ownerEmail: userUuid,
            },
          },
        });

        notify('Task owner saved successfully', {
          variant: 'success',
        });
      } catch (e) {
        console.error('Error updating task owner:', e);
        notify(
          `Error saving: ${e instanceof Error ? e.message : 'Unknown error'}`,
          {
            variant: 'error',
          }
        );

        // Note: Form state reversion would need to be handled by the AssigneeDropdown component
        // if we wanted to revert on error, but for now we'll keep the optimistic update
      }
    },
    [upsertTask, claimUuid, notify]
  );

  // Handle updating task priority
  const handleUpdateTaskPriority = useCallback(
    async (taskId: string, priorityValue: TaskPriority) => {
      // Don't make API calls for temporary tasks
      if (
        taskId === 'additional-task-default' ||
        taskId.startsWith('additional-task-')
      ) {
        return;
      }

      // Default to HIGH if no priority value
      const finalPriorityValue = priorityValue || TaskPriority.High;

      notify('Updating task priority...', {
        variant: 'info',
      });

      try {
        await upsertTask({
          variables: {
            details: {
              taskUuid: taskId,
              targetArea: TargetAreaType.Claim,
              priority: finalPriorityValue,
            },
          },
        });

        notify('Priority updated successfully', {
          variant: 'success',
        });
      } catch (error) {
        console.error('Error updating task priority:', error);
        notify(
          `Failed to update priority: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          {
            variant: 'error',
          }
        );
      }
    },
    [upsertTask, notify]
  );

  useEffect(() => {
    if (coreTasksData?.tasks?.edges?.length && isFirstRender) {
      setIsFirstRender(false);
    }
  }, [coreTasksData, isFirstRender]);

  // Additional tasks state for new task form
  const newAdditionalTask = {
    id: `additional-task-default`,
    task: '',
    assignee: '',
    priority: '',
    dueDate: '',
    status: false,
  };

  // State for API-fetched additional tasks

  // Process tasks data when it's loaded - filter by task type on the frontend
  useEffect(() => {
    if (coreTasksData?.tasks?.edges) {
      const coreTasksList = [];
      const additionalTasksList = [];

      // Process all tasks and separate them by type
      for (const edge of coreTasksData.tasks.edges) {
        const task = edge.node;
        const assigneeId = task.user?.node?.userId;
        // Always default to HIGH priority if not specified
        const taskPriority = task.priority || 'HIGH';

        const processedTask = {
          id: task.uuid,
          name: task.label,
          task: task.label, // For compatibility with AdditionalTaskRow component
          assignee: assigneeId,
          priority: taskPriority, // Default to High if not provided
          dueDate: formatDueDate(task.dueDate) || '', // Default date if not provided
          completedDate: formatDueDate(task.completedDate) || '', // Include completed date
          note: task.note,
          status: task.status,
          userUuid: task.user?.node?.uuid,
          type: task.type,
          system: task.system, // Pass through the system field
        };

        if (
          task.type === TaskType.DefaultTask ||
          task.type === TaskType.CoreTask
        ) {
          coreTasksList.push(processedTask);
        } else if (task.type === TaskType.AdditionalTask) {
          // For additional tasks, convert status to boolean for compatibility
          additionalTasksList.push({
            ...processedTask,
            status: task.status === TaskStatus.Done, // Convert to boolean for compatibility
          });
        }
      }

      setCoreTasks(coreTasksList);
      setAdditionalTasks(additionalTasksList);
    }
  }, [coreTasksData]);

  // Placeholder task options for future use
  // const taskOptions = [
  //   { value: 'acknowledge_fnol', label: 'Acknowledge FNOL' },
  //   { value: 'select_vendors', label: 'Select Vendors' },
  //   {
  //     value: 'coverage_investigation',
  //     label: 'Initial Coverage Investigation',
  //   },
  //   { value: 'set_reserve', label: 'Set Initial Reserve' },
  //   { value: 'send_letter', label: 'Send Closing Letter' },
  //   { value: 'contact_insured', label: 'Contact Insured' },
  //   { value: 'review_documents', label: 'Review Documents' },
  //   { value: 'update_status', label: 'Update Claim Status' },
  // ];

  const priorityOptions = [
    { value: 'HIGH', label: 'High' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'LOW', label: 'Low' },
  ];

  const handleCoreTasksAccordion = (
    _event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setCoreTasksOpen(newExpanded);
  };

  const handleAdditionalTasksAccordion = (
    _event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setAdditionalTasksOpen(newExpanded);
  };

  const handleBellClick = useCallback(
    (taskId: string) => {
      setOpenTextAreaId(openTextAreaId === taskId ? null : taskId);
    },
    [openTextAreaId]
  );

  const validateSetErrors = (
    name: string,
    dateToValidate: Date | null,
    value: string,
    validateDate: ValidateDateType,
    formMethods: DateFieldProps['formMethods']
  ) => {
    const isValidDate =
      dateToValidate instanceof Date && !isNaN(dateToValidate.getTime());
    if ((value && value.length > 9) || isValidDate) {
      const { isInvalid, errorMessage } = validateDate(dateToValidate, value);
      if (isInvalid) {
        formMethods.setError(name, {
          type: 'validation',
          message: errorMessage,
        });
      } else {
        formMethods.clearErrors(name);
      }
      return isInvalid;
    }
    return true;
  };

  // TaskTableHeader moved to task-table.tsx component

  const TaskNotesTextarea = ({ taskId }: { taskId: string }) => {
    const noteFieldName = `notes-${taskId}`;
    const { getValues } = useFormContext();

    const handleSaveNote = async () => {
      const noteValue = getValues(noteFieldName);

      notify('Saving note...', {
        variant: 'info',
      });

      try {
        // Update task note in the API
        await upsertTask({
          variables: {
            details: {
              taskUuid: taskId,
              targetArea: TargetAreaType.Claim,
              note: noteValue,
            },
          },
        });

        notify('Note saved successfully', {
          variant: 'success',
        });
        setOpenTextAreaId(null);
      } catch (error) {
        console.error('Error saving note:', error);
        notify(
          `Failed to save note: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          {
            variant: 'error',
          }
        );
      }
    };

    return (
      <Box className="notes-container">
        <NewFormTextField
          name={noteFieldName}
          multiline={true}
          className="task-notes-textarea"
          rows={2}
          containerWidth={6}
          hiddenLabel={true}
          disableHoverBorder={isNotActive ? true : false}
          InputProps={{
            readOnly: isNotActive,
          }}
        />
        <Box>
          <button
            type="button"
            className="save-reminder-button"
            onClick={handleSaveNote}
            disabled={isNotActive}
            style={{
              cursor: isNotActive ? 'default' : 'pointer',
              color: isNotActive ? '#999' : 'black',
              pointerEvents: isNotActive ? 'none' : 'auto',
            }}
          >
            Save Reminder
          </button>
        </Box>
      </Box>
    );
  };
  const ReminderBellIcon = ({ taskId }: { taskId: string }) => {
    const { getValues } = useFormContext();

    // Use a ref to store the note value and only update the UI when clicked
    const noteValueRef = React.useRef(getValues(`notes-${taskId}`));

    // Handle click to show the notes textarea
    const handleClick = () => {
      // Update the ref value before showing notes
      noteValueRef.current = getValues(`notes-${taskId}`);
      handleBellClick(taskId);
    };

    return (
      <IconButton
        onClick={handleClick}
        className="reminder-bell-icon"
        sx={{
          pointerEvents: 'auto !important',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        {noteValueRef.current ? (
          <NotificationsActiveOutlinedIcon
            sx={{ color: 'black', pointerEvents: 'auto !important' }}
          />
        ) : (
          <AddAlertOutlined
            sx={{ color: 'black', pointerEvents: 'auto !important' }}
          />
        )}
      </IconButton>
    );
  };

  // TaskTable moved to task-table.tsx component
  const CoreTaskRow = ({
    task,
    isFirstRender,
    onUpsertTask,
    formMethods,
    initializeTask,
    minDate,
  }: {
    task: any;
    isFirstRender: boolean;
    onUpsertTask: (taskId: string, value: string, taskType?: TaskType) => any;
    formMethods: any;
    initializeTask: (task: any) => void;
    minDate?: Date;
  }) => {
    // Track when user has manually changed the assignee
    const userChangedAssigneeRef = React.useRef(false);

    // Initialize this task's form fields once
    useEffect(() => {
      initializeTask(task);
    }, [task, initializeTask]);

    // Watch form values for checkbox states and errors
    const naValue = formMethods.watch(`na-${task.id}`);
    const completedValue = formMethods.watch(`completed-${task.id}`);

    const dateFieldError = formMethods.formState.errors[`date-${task.id}`];
    const hasDateError = !!dateFieldError;
    const completedDateFieldError =
      formMethods.formState.errors[`completed-date-${task.id}`];
    const hasCompletedDateError = !!completedDateFieldError;
    // Simple sync with API data - only for non-assignee fields
    const prevTaskRef = React.useRef({ ...task });

    React.useEffect(() => {
      // Update assignee from API data, but only if user hasn't manually changed it
      const newAssignee = getTaskAssignee(task);
      if (
        prevTaskRef.current.assignee !== newAssignee &&
        !userChangedAssigneeRef.current
      ) {
        formMethods.setValue(`assignee-${task.id}`, newAssignee, {
          shouldDirty: false,
        });
      }

      // Update other fields from API
      if (prevTaskRef.current.priority !== task.priority) {
        formMethods.setValue(`priority-${task.id}`, task.priority || 'HIGH', {
          shouldDirty: false,
        });
      }

      if (prevTaskRef.current.dueDate !== task.dueDate) {
        formMethods.setValue(`date-${task.id}`, task.dueDate || '', {
          shouldDirty: false,
        });
      }

      if (prevTaskRef.current.completedDate !== task.completedDate) {
        formMethods.setValue(
          `completed-date-${task.id}`,
          task.completedDate || '',
          {
            shouldDirty: false,
          }
        );
      }

      if (prevTaskRef.current.note !== task.note) {
        formMethods.setValue(`notes-${task.id}`, task.note || '', {
          shouldDirty: false,
        });
      }

      // Update status-related fields
      const newNA = getTaskNAValue(task.status);
      const newCompleted = getTaskCompletedValue(task.status);

      if (prevTaskRef.current.status !== task.status) {
        formMethods.setValue(`na-${task.id}`, newNA, {
          shouldDirty: false,
        });
        formMethods.setValue(`completed-${task.id}`, newCompleted, {
          shouldDirty: false,
        });
      }

      // Reset user change tracking if API has confirmed the user's change
      const currentFormAssignee = formMethods.getValues(`assignee-${task.id}`);
      if (
        userChangedAssigneeRef.current &&
        currentFormAssignee === newAssignee
      ) {
        userChangedAssigneeRef.current = false;
      }

      // Update the ref
      prevTaskRef.current = { ...task };
    }, [task, formMethods]);

    // Create a custom handleUpdateTaskOwner that tracks user changes
    const handleUpdateTaskOwnerForThisTask = React.useCallback(
      (event: SelectChangeEvent<unknown>, taskUuid: string) => {
        // Mark that user has manually changed the assignee
        userChangedAssigneeRef.current = true;

        // Call the original API handler
        return handleUpdateTaskOwner(event, taskUuid);
      },
      []
    );

    // No need for useEffect to apply styling - React's declarative approach will handle it
    // The naStatusMap state drives the className and sx prop in the JSX below

    // We'll use our separate state management instead of watching the form
    // Keeping the line as a comment for reference
    // const isNAChecked = formMethods.watch(`na-${task.id}`);

    const assigneeOptionsList = useMemo(() => {
      return assigneeOptions;
    }, []);
    // Check task completion states - they are mutually exclusive
    // Check both task data and form data for completed date
    const taskHasCompletedDate = !!(
      task.completedDate && task.completedDate.trim()
    );
    const formCompletedDate = formMethods.getValues(
      `completed-date-${task.id}`
    );
    const hasCompletedDate =
      taskHasCompletedDate ||
      !!(formCompletedDate && String(formCompletedDate).trim());
    const isNA = naValue;

    // If either is selected, disable all other fields
    const isTaskCompleted = hasCompletedDate || isNA;

    // For styling purposes
    const tableDataClass = `${isTaskCompleted ? 'inactive-cell' : ''}`;

    // Check if task is overdue
    const isOverdue = isDateOverdue(task.dueDate);
    return (
      <FormProvider {...formMethods}>
        <React.Fragment key={task.id}>
          <Box
            className={`new-customized-table-data-row-container ${
              isTaskCompleted ? 'disabled-row' : isOverdue ? 'overdue-row' : ''
            }`}
            data-na={naValue ? 'true' : 'false'}
            data-completed={completedValue ? 'true' : 'false'}
            data-task-id={task.id}
            data-testid={`task-row-${task.id}`}
            sx={{
              flexDirection: 'column',

              borderBottom:
                openTextAreaId === task.id ? 'none' : '1px solid red',
              marginBottom: openTextAreaId === task.id ? 0 : '2px',
              '@media (max-width: 768px)': {
                flexDirection: 'column',
                gap: 1,
              },
            }}
          >
            {/* Main row content */}
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                gap: { xs: 1, md: 2 },
                paddingBottom:
                  hasDateError || hasCompletedDateError ? '25px' : '0px',
              }}
            >
              <Box
                className="new-customized-table-data-column"
                sx={{
                  width: '1px',
                  flexShrink: 0,
                  textAlign: 'left',
                  opacity: isNotActive ? 0.5 : 1, // Reduced opacity only when claim is closed/inactive
                  pointerEvents: 'auto !important', // Always keep bell icon clickable
                }}
              >
                <ReminderBellIcon taskId={task.id} />
              </Box>
              <Box
                className={`new-customized-table-data-column ${tableDataClass}`}
                sx={{
                  width: { xs: '100%', sm: '21%', md: '19%', lg: '17%' },
                  flexShrink: 0,
                  textAlign: 'left',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  className="task-text--bold"
                  style={{
                    color: isTaskCompleted ? 'rgba(0, 0, 0, 0.55)' : 'inherit',
                    opacity: !isTaskCompleted && isNotActive ? '0.4' : '1',
                    fontSize: '14px',
                  }}
                >
                  {task.name}
                </Typography>
              </Box>
              <Box
                className={`new-customized-table-data-column ${tableDataClass}`}
                sx={{
                  width: { xs: '100%', sm: '15%', md: '13%', lg: '11%' },
                  flexShrink: 0,
                  textAlign: 'left',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <DiaryAssigneeDropdown
                  taskId={task.id}
                  options={assigneeOptionsList}
                  hideBorders
                  disabled={isTaskCompleted || isNotActive}
                  onUpdateTaskOwner={handleUpdateTaskOwnerForThisTask}
                  isNotActive={!isTaskCompleted && isNotActive}
                />
              </Box>
              <Box
                className={`new-customized-table-data-column ${tableDataClass}`}
                sx={{
                  width: { xs: '100%', sm: '15%', md: '13%', lg: '11%' },
                  flexShrink: 0,
                  textAlign: 'left',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <PriorityDropdown
                  taskId={task.id}
                  options={priorityOptions}
                  initialValue={task.priority || 'HIGH'}
                  disabled={isTaskCompleted || isNotActive}
                  onChange={async (taskId, value) => {
                    // We don't need to update local state for core tasks as we'll refetch from API
                    // The form state will be updated by the PriorityDropdown component
                  }}
                  handleUpdateTaskPriority={handleUpdateTaskPriority}
                  isNotActive={!isTaskCompleted && isNotActive}
                />
              </Box>
              <Box
                className={`new-customized-table-data-column ${tableDataClass}`}
                sx={{
                  width: { xs: '100%', sm: '15%', md: '13%', lg: '11%' },
                  flexShrink: 0,
                  textAlign: 'left',
                  position: 'relative',
                  paddingBottom: '20px',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <DateField
                  taskId={task.id}
                  isDisabled={isTaskCompleted || isNotActive}
                  isNotActive={isNotActive}
                  hasCompletedDate={hasCompletedDate}
                  isNA={isNA}
                  minDate={minDate}
                  onChange={async (taskId, date, value) => {
                    const name = `date-${taskId}`;
                    const dateToValidate = date ? new Date(date) : null;
                    const isInvalid = validateSetErrors(
                      name,
                      dateToValidate,
                      value,
                      validateFutureDate,
                      formMethods
                    );

                    if (!isInvalid && date) {
                      handleUpdateDueDate(taskId, date);
                    }
                  }}
                />
              </Box>
              <Box
                className={`new-customized-table-data-column status-cell`}
                sx={{
                  width: { xs: '100%', sm: '40%', md: '35%', lg: '30%' },
                  flexShrink: 0,
                  textAlign: 'left',
                  borderColor: !isTaskCompleted && isNotActive ? '#666' : '',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minHeight: '60px',
                  marginBottom: '0px',
                }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',

                    width: '100%',

                    marginLeft: '10px',
                    opacity: '1',
                  }}
                >
                  {task.name !== 'Check Claim Status' ? (
                    <DateField
                      taskId={task.id}
                      isDisabled={isNA || isNotActive}
                      isNotActive={isNotActive}
                      maxDate={new Date()}
                      fieldName={`completed-date-${task.id}`}
                      isCompletedDate={true}
                      formMethods={formMethods}
                      onChange={async (taskId, date, value) => {
                        if (!value)
                          handleUpdateCompletedDate(taskId, date, false);
                        const name = `completed-date-${taskId}`;
                        const dateToValidate = date ? new Date(date) : null;

                        const isInvalid = validateSetErrors(
                          name,
                          dateToValidate,
                          value,
                          validatePastDate,
                          formMethods
                        );
                        if (!isInvalid && date) {
                          formMethods.setValue(`na-${taskId}`, false, {
                            shouldDirty: false,
                          });
                          handleUpdateCompletedDate(taskId, date, true);
                        }
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '168px',
                        display: 'flex',
                        alignItems: 'left',
                        justifyContent: 'left',
                        marginLeft: '10px',
                      }}
                    >
                      <Typography style={{ fontSize: '24px' }}>-</Typography>
                    </Box>
                  )}

                  <Box
                    className={`${
                      hasCompletedDate ? 'disabled-checkbox' : ''
                    } checkbox-container na-checkbox-container `}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      zIndex: 0,
                      position: 'relative',
                      pointerEvents:
                        hasCompletedDate || isNotActive ? 'none' : 'auto',
                      opacity: hasCompletedDate || isNotActive ? 0.5 : 1,
                    }}
                  >
                    {task.name !== 'Check Claim Status' ? (
                      <>
                        <Box>
                          <Controller
                            name={`na-${task.id}`}
                            control={formMethods.control}
                            render={({ field: { onChange, value } }) => {
                              // Define a single handler function with debounce logic
                              const handleNAChange = async () => {
                                // Don't execute if disabled
                                if (hasCompletedDate || isNotActive) return;

                                const newValue = !value;

                                // Update local form state first
                                onChange(newValue);

                                // If N/A is checked, clear completed date and uncheck Completed form value
                                if (newValue) {
                                  formMethods.setValue(
                                    `completed-${task.id}`,
                                    false,
                                    {
                                      shouldDirty: false,
                                    }
                                  );
                                  formMethods.setValue(
                                    `completed-date-${task.id}`,
                                    '',
                                    {
                                      shouldDirty: false,
                                    }
                                  );
                                }

                                // Notify user
                                notify('Updating task...', {
                                  variant: 'info',
                                });

                                // Update task status in the API
                                try {
                                  await upsertTask({
                                    variables: {
                                      details: {
                                        taskUuid: task.id,
                                        targetArea: TargetAreaType.Claim,
                                        status: newValue
                                          ? TaskStatus.NotApplicable
                                          : TaskStatus.ToDo,
                                        // Clear completed date when N/A is selected
                                        completedDate: newValue
                                          ? null
                                          : undefined,
                                      },
                                    },
                                  });

                                  notify(
                                    newValue
                                      ? 'Task marked as not applicable'
                                      : 'Task status updated to To-Do',
                                    { variant: 'success' }
                                  );
                                } catch (error) {
                                  console.error(
                                    'Error updating task status:',
                                    error
                                  );
                                  notify(
                                    `Failed to update task status: ${
                                      error instanceof Error
                                        ? error.message
                                        : 'Unknown error'
                                    }`,
                                    { variant: 'error' }
                                  );

                                  // Revert local state on error
                                  onChange(!newValue);
                                }
                              };

                              return (
                                <ConvergeCheckbox
                                  name={`na-${task.id}`}
                                  checked={!!value}
                                  onChange={handleNAChange}
                                  dataTestId={`task-na-${task.id}`}
                                  disabled={hasCompletedDate || isNotActive}
                                  className={
                                    isNotActive ? 'disabled-inactive' : ''
                                  }
                                />
                              );
                            }}
                          />
                        </Box>
                        <Typography
                          className="task-text--bold checkbox-text"
                          style={{
                            fontSize: '14px',
                            opacity: hasCompletedDate || isNotActive ? 0.5 : 1,
                          }}
                        >
                          N/A
                        </Typography>
                      </>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                        }}
                      >
                        <Typography style={{ fontSize: '24px' }}>-</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
            {openTextAreaId === task.id && (
              <Box
                sx={{
                  width: '100%',
                  marginTop: 1,
                  paddingBottom: '8px',
                  boxSizing: 'border-box',
                  backgroundColor: 'white',
                  padding: '8px 16px',
                }}
              >
                <TaskNotesTextarea taskId={task.id} />
              </Box>
            )}
          </Box>
        </React.Fragment>
      </FormProvider>
    );
  };

  const AdditionalTaskRow = ({
    task,
    onUpsertTask,
    formMethods,
    initializeTask,
    minDate,
    isNotActive,
  }: {
    task: any;
    onUpsertTask: (taskId: string, value: string, taskType?: TaskType) => any;
    formMethods: any;
    initializeTask: (task: any) => void;
    minDate?: Date;
    isNotActive?: boolean;
  }) => {
    // Initialize this task's form fields once
    useEffect(() => {
      initializeTask(task);
    }, [task, initializeTask]);

    // Check if task name is empty (task hasn't been sent to backend)
    const taskName = task.task || '';
    const isTaskNameEmpty = !taskName.trim();

    // Use form data for completed date since it's not in the GraphQL query
    const formCompletedDate = formMethods.getValues(
      `completed-date-${task.id}`
    );
    const hasCompletedDate = !!(
      formCompletedDate && String(formCompletedDate).trim()
    );

    // Watch for validation errors on the date field
    const dateFieldError = formMethods.formState.errors[`date-${task.id}`];
    const hasDateError = !!dateFieldError;
    const completedDateFieldError =
      formMethods.formState.errors[`completed-date-${task.id}`];
    const hasCompletedDateError = !!completedDateFieldError;

    // For additional tasks, only disable non-task fields when task name is empty
    // Don't disable fields just because there's a completed date (unlike core tasks)
    const shouldDisableNonTaskFields = isTaskNameEmpty;
    const shouldDisableTaskName = false;
    // Memoize the options to prevent unnecessary re-renders
    const assigneeOptionsList = useMemo(() => assigneeOptions, []);
    const priorityOptionsList = useMemo(() => priorityOptions, []);

    // Check if task is overdue
    const isOverdue = isDateOverdue(task.dueDate);

    // Task completion state for styling
    const isTaskCompleted = hasCompletedDate;

    return (
      <FormProvider {...formMethods}>
        <Box
          key={task.id}
          className={`new-customized-table-data-row-container  additional-task ${
            isTaskCompleted ? 'disabled-row' : isOverdue ? 'overdue-row' : ''
          } `}
          data-completed={isTaskCompleted ? 'true' : 'false'}
          data-task-id={task.id}
          data-testid={`task-row-${task.id}`}
          sx={{
            flexDirection: 'column',
            opacity: isTaskCompleted ? 0.8 : 1,
            backgroundColor: isTaskCompleted
              ? '#f9f9f9'
              : isOverdue
              ? '#FDEDED'
              : 'transparent',
            borderBottom: openTextAreaId === task.id ? 'none' : '1px solid red',
            marginBottom: openTextAreaId === task.id ? 0 : '2px',
            '@media (max-width: 768px)': {
              flexDirection: 'column',
              gap: 1,
            },
          }}
        >
          {/* Main row content */}
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              gap: { xs: 1, md: 2 },
              paddingBottom:
                hasDateError || hasCompletedDateError ? '25px' : '0px',
            }}
          >
            <Box
              className="new-customized-table-data-column"
              sx={{
                width: '1px',
                flexShrink: 0,
                textAlign: 'left',
                opacity: isNotActive ? 0.5 : 1, // Reduced opacity only when claim is closed/inactive
                pointerEvents: 'auto !important', // Always keep bell icon clickable
              }}
            >
              <ReminderBellIcon taskId={task.id} />
            </Box>
            <Box
              className={`new-customized-table-data-column task-dropdown ${
                isTaskCompleted ? 'inactive-cell' : ''
              }`}
              sx={{
                width: { xs: '100%', sm: '16%', md: '14%', lg: '12%' },
                flexShrink: 0,
                textAlign: 'left',
                minHeight: '60px',
                display: 'flex',
                alignItems: 'center',
                '& .MuiOutlinedInput-root': {
                  height: '37px',
                  '& .MuiInputBase-input': {
                    padding: '10px 12px !important',
                  },
                  '&.Mui-disabled': {
                    '& .MuiInputBase-input': {
                      padding: '10px 12px !important',
                      color:
                        !shouldDisableTaskName && isNotActive
                          ? '#939292ff'
                          : '#939292ff',
                      '-webkit-text-fill-color': `${
                        !shouldDisableTaskName && isNotActive
                          ? 'rgba(131, 130, 130, 0.55)'
                          : '#939292ff'
                      } !important`,
                    },
                  },
                },
              }}
            >
              <TaskTextField
                taskId={task.id}
                initialValue={task.task || ''}
                disabled={isNotActive || isTaskCompleted}
                onChange={(taskId, value) => {
                  setAdditionalTasks((prev) =>
                    prev.map((t) =>
                      t.id === taskId ? { ...t, task: value } : t
                    )
                  );
                }}
                onUpsertTask={handleUpsertTask}
                taskType={TaskType.AdditionalTask}
                isNotActive={isNotActive && !isTaskCompleted}
              />
            </Box>
            <Box
              className={`assignee-dropdown ${
                isTaskCompleted ? 'inactive-cell' : ''
              }`}
              sx={{
                width: { xs: '100%', sm: '15%', md: '13%', lg: '11%' },
                flexShrink: 0,
                textAlign: 'left',
                minHeight: '60px',
                display: 'flex',
                alignItems: 'center',
                marginRight: '40px',
                marginTop: '0px',
                marginBottom: '2px',
              }}
            >
              <DiaryAssigneeDropdown
                taskId={task.id}
                options={assigneeOptionsList}
                disabled={
                  shouldDisableNonTaskFields || isNotActive || isTaskCompleted
                }
                onUpdateTaskOwner={handleUpdateTaskOwner}
                isNotActive={
                  isNotActive && !shouldDisableNonTaskFields && !isTaskCompleted
                }
              />
            </Box>
            <Box
              className={`priority-dropdown ${
                isTaskCompleted ? 'inactive-cell' : ''
              }`}
              sx={{
                width: { xs: '100%', sm: '15%', md: '13%', lg: '11%' },
                flexShrink: 0,
                textAlign: 'left',
                minHeight: '60px',
                display: 'flex',
                alignItems: 'center',
                marginRight: '20px',
                marginTop: '0px',
                marginBottom: '2px',
              }}
            >
              <PriorityDropdown
                taskId={task.id}
                options={priorityOptionsList}
                disabled={
                  shouldDisableNonTaskFields || isNotActive || isTaskCompleted
                }
                isNotActive={
                  isNotActive && !shouldDisableNonTaskFields && !isTaskCompleted
                }
                initialValue={task.priority || ''}
                onChange={(taskId, value) => {
                  // Only update local state for UI consistency
                  // API call is handled by handleUpdateTaskPriority
                  setAdditionalTasks((prev) =>
                    prev.map((t) =>
                      t.id === taskId ? { ...t, priority: value } : t
                    )
                  );
                }}
                handleUpdateTaskPriority={handleUpdateTaskPriority}
              />
            </Box>
            <Box
              className={`new-customized-table-data-column due-date ${
                isTaskCompleted ? 'inactive-cell' : ''
              }`}
              sx={{
                width: { xs: '100%', sm: '15%', md: '13%', lg: '11%' },
                flexShrink: 0,
                textAlign: 'left',
                position: 'relative',
                paddingBottom: '20px',
                minHeight: '60px',
                display: 'flex',
                alignItems: 'center',
                opacity:
                  shouldDisableNonTaskFields || isNotActive || isTaskCompleted
                    ? 0.8
                    : 1,
              }}
            >
              <Box sx={{ marginBottom: '0px' }}>
                <DateField
                  isNotActive={
                    isNotActive &&
                    !shouldDisableNonTaskFields &&
                    !isTaskCompleted
                  }
                  taskId={task.id}
                  isDisabled={
                    shouldDisableNonTaskFields || isNotActive || isTaskCompleted
                  }
                  hasCompletedDate={hasCompletedDate}
                  minDate={minDate}
                  onChange={async (taskId, date, value) => {
                    const name = `date-${taskId}`;
                    const dateToValidate = date ? new Date(date) : null;

                    const isInvalid = validateSetErrors(
                      name,
                      dateToValidate,
                      value,
                      validateFutureDate,
                      formMethods
                    );

                    if (!isInvalid && date) {
                      // Update local state for due date only (don't change status for due date)
                      setAdditionalTasks((prev) =>
                        prev.map((t) =>
                          t.id === taskId ? { ...t, dueDate: value } : t
                        )
                      );

                      // Don't call API for temporary tasks
                      if (
                        task.id === 'additional-task-default' ||
                        task.id.startsWith('additional-task-')
                      ) {
                        return;
                      }

                      handleUpdateDueDate(taskId, date);
                    }
                  }}
                />
              </Box>
            </Box>
            <Box
              className={`status-cell ${task.status}`}
              sx={{
                width: { xs: '100%', sm: '40%', md: '35%', lg: '30%' },
                minHeight: '60px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                marginTop: '5px',
                marginLeft: '30px',
                opacity: shouldDisableNonTaskFields || isNotActive ? 0.8 : 1,
              }}
            >
              <Box sx={{ marginBottom: '10px' }}>
                <DateField
                  taskId={task.id}
                  isDisabled={shouldDisableNonTaskFields || isNotActive}
                  isNotActive={isNotActive && !shouldDisableNonTaskFields}
                  maxDate={new Date()}
                  fieldName={`completed-date-${task.id}`}
                  isCompletedDate={true}
                  formMethods={formMethods}
                  onChange={async (taskId, date, value) => {
                    if (!value) handleUpdateCompletedDate(taskId, date, false);

                    const name = `completed-date-${taskId}`;
                    const dateToValidate = date ? new Date(date) : null;

                    const isInvalid = validateSetErrors(
                      name,
                      dateToValidate,
                      value,
                      validatePastDate,
                      formMethods
                    );

                    // When completed date is selected, mark as completed; when cleared, mark as incomplete

                    // Don't call API for temporary tasks
                    if (
                      taskId === 'additional-task-default' ||
                      taskId.startsWith('additional-task-')
                    ) {
                      return;
                    }
                    if (!isInvalid && date) {
                      setAdditionalTasks((prev) =>
                        prev.map((t) =>
                          t.id === taskId
                            ? {
                                ...t,
                                status: false,
                                completedDate: value,
                              }
                            : t
                        )
                      );
                      handleUpdateCompletedDate(taskId, date, true);
                    }
                  }}
                />
              </Box>

              <Box
                className="delete-icon-container delete-task"
                sx={{
                  pointerEvents:
                    'auto !important' /* Ensure the delete button is always clickable */,
                  zIndex: 0,
                  position: 'relative',
                }}
              >
                <IconButton
                  size="medium"
                  color="error"
                  onClick={() => handleDeleteTask(task.id)}
                  className="delete-button"
                  disabled={hasCompletedDate || isNotActive}
                  sx={{
                    opacity: hasCompletedDate || isNotActive ? 0.3 : 1,
                    pointerEvents:
                      hasCompletedDate || isNotActive
                        ? 'none'
                        : 'auto !important',
                    '&:hover': {
                      backgroundColor:
                        'rgba(211, 47, 47, 0.04)' /* Light red background on hover */,
                    },
                  }}
                >
                  <CancelOutlined className="cancel-icon" />
                </IconButton>
              </Box>
            </Box>
          </Box>
          {openTextAreaId === task.id && (
            <Box
              sx={{
                width: '100%',
                marginTop: 1,
                paddingBottom: '8px',
                boxSizing: 'border-box',
                backgroundColor: 'white',
                padding: '8px 16px',
              }}
            >
              <TaskNotesTextarea taskId={task.id} />
            </Box>
          )}
        </Box>
      </FormProvider>
    );
  };

  interface DateFieldProps {
    taskId: string;
    isDisabled?: boolean;
    isNotActive?: boolean;
    minDate?: Date;
    maxDate?: Date;
    fieldName?: string;
    onChange: (taskId: string, date: Date | null, value: string) => void;
    isCompletedDate?: boolean;
    hasCompletedDate?: boolean;
    isNA?: boolean;
    formMethods?: any; // Add form methods to access errors
  }

  const DateField = ({
    taskId,
    onChange,
    isDisabled,
    isNotActive,
    minDate,
    maxDate,
    fieldName,
    isCompletedDate,
    hasCompletedDate,
    isNA,
    formMethods,
  }: DateFieldProps) => {
    // Create a stable reference to the onDateChange function
    const handleDateChange = useCallback(
      (date: Date | null, inputValue?: string) => {
        onChange(taskId, date, inputValue || '');
      },
      [taskId, onChange]
    );

    // Get error information from form methods if available
    const fieldNameForError = fieldName || `date-${taskId}`;
    const fieldError = formMethods?.formState?.errors?.[fieldNameForError];
    const hasError = !!fieldError;
    const errorMessage = fieldError?.message || '';

    return (
      <NewDateField
        disabled={isDisabled ?? false}
        name={fieldName || `date-${taskId}`}
        containerWidth={12}
        label=""
        maxDate={maxDate}
        minDate={minDate}
        onDateChange={handleDateChange}
        isCompletedDate={isCompletedDate}
        disableValidation={true}
        helperText={hasError ? errorMessage : ''}
        error={hasError}
        sx={{
          width: isCompletedDate ? '174px' : '170px',
          height: '37px',
          '.MuiOutlinedInput-root': {
            height: '37px',
            '& fieldset': {
              border: '1px #666 solid',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#23a892',
            },
            '&.Mui-disabled fieldset': {
              borderColor: (() => {
                const hasOtherDisableConditions =
                  isNA || (hasCompletedDate && !isCompletedDate);
                const isBothConditions =
                  isNotActive && hasOtherDisableConditions;

                return isBothConditions
                  ? '#b0b0b0ff' // Darker border when both conditions
                  : isNotActive
                  ? '#d7d6d6ff'
                  : '#bdbdbdff'; // Lighter border for single condition
              })(),
            },
            '&.Mui-disabled': {
              '& .MuiInputBase-input': {
                height: '20px',
                backgroundColor: (() => {
                  // For due date fields, check if disabled by both isNotActive AND other conditions
                  const hasOtherDisableConditions =
                    isNA || (hasCompletedDate && !isCompletedDate);

                  const isOnlyNotActive =
                    isNotActive && !hasOtherDisableConditions;

                  return isOnlyNotActive
                    ? 'rgba(245, 243, 243, 0.4)' // Lighter when ONLY isNotActive
                    : 'rgba(239, 236, 236, 0.55)'; // Standard disabled (both conditions or only other conditions)
                })(),
                color: (() => {
                  const hasOtherDisableConditions =
                    isNA || (hasCompletedDate && !isCompletedDate);
                  const isOnlyNotActive =
                    isNotActive && !hasOtherDisableConditions;

                  return isOnlyNotActive
                    ? 'rgba(131, 130, 130, 0.4)'
                    : 'rgba(131, 130, 130, 0.55)';
                })(),
                '-webkit-text-fill-color': (() => {
                  const hasOtherDisableConditions =
                    isNA || (hasCompletedDate && !isCompletedDate);
                  const isOnlyNotActive =
                    isNotActive && !hasOtherDisableConditions;

                  return isOnlyNotActive
                    ? 'rgba(164, 161, 161, 0.4)'
                    : 'rgba(164, 161, 161, 0.55)';
                })(),
              },
              '& .MuiIconButton-root': {
                backgroundColor: (() => {
                  const hasOtherDisableConditions =
                    isNA || (hasCompletedDate && !isCompletedDate);
                  const isOnlyNotActive =
                    isNotActive && !hasOtherDisableConditions;

                  return isOnlyNotActive
                    ? 'rgba(245, 243, 243, 0.4)' // Same as field background when ONLY isNotActive
                    : 'rgba(239, 236, 236, 0.55)'; // Same as field background when both conditions
                })(),
                '& svg': {
                  opacity: (() => {
                    const hasOtherDisableConditions =
                      isNA || (hasCompletedDate && !isCompletedDate);
                    const isOnlyNotActive =
                      isNotActive && !hasOtherDisableConditions;

                    return isOnlyNotActive ? '0.3' : '0.4';
                  })(),
                },
              },
            },
          },
          '.MuiButtonBase-root.MuiIconButton-root.MuiIconButton-edgeEnd.MuiIconButton-sizeMedium':
            {
              marginRight: '3px',
            },

          '& .MuiFormHelperText-root': {
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            margin: 0,
            padding: '2px 4px',
            fontSize: '11px',
            lineHeight: '1.2',
            zIndex: 10,
            fontWeight: '600 !important',
          },
          position: 'relative',
        }}
      />
    );
  };

  return (
    <ClaimEntityPageContainer className="dairy-container" title="Diary">
      {/* Core Tasks Section */}
      <ConvergeDetailAccordion
        title="CORE TASKS"
        description="These tasks were created when the claim was opened. Select N/A if the task is not required for this claim."
        expanded={isCoreTasksOpen}
        onChange={handleCoreTasksAccordion}
        className="accordion-width"
        detailsClassName="accordion-details-style"
        testId="core-tasks-accordion"
      >
        <CardSection className="core-tasks-card">
          {coreTasksLoading && isFirstRender ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 4,
                margin: 'auto',
              }}
            >
              <CircularProgress />
            </Box>
          ) : coreTasksError ? (
            <Box sx={{ p: 2, color: 'error.main' }}>
              Error loading core tasks: {coreTasksError.message}
            </Box>
          ) : coreTasks.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography>No core tasks found for this claim.</Typography>
            </Box>
          ) : (
            <TaskTable
              tasks={coreTasks}
              headerType="core"
              renderRow={(task) => (
                <CoreTaskRow
                  key={task['id'] as string}
                  task={task}
                  isFirstRender={isFirstRender}
                  onUpsertTask={handleUpsertTask}
                  formMethods={masterFormMethods}
                  initializeTask={initializeTaskFormFields}
                  minDate={minDate}
                />
              )}
            />
          )}
        </CardSection>
      </ConvergeDetailAccordion>
      {/* Additional Tasks Section */}
      <Box sx={{ marginTop: '32px' }}>
        <ConvergeDetailAccordion
          title="ADDITIONAL TASKS"
          description="Create as many tasks as needed to complete this claim."
          expanded={isAdditionalTasksOpen}
          onChange={handleAdditionalTasksAccordion}
          className="accordion-width"
          detailsClassName="accordion-details-style"
          testId="additional-tasks-accordion"
        >
          <CardSection>
            {coreTasksLoading && isFirstRender ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  py: 4,
                  margin: 'auto',
                }}
              >
                <CircularProgress />
              </Box>
            ) : coreTasksError ? (
              <Box sx={{ p: 2, color: 'error.main' }}>
                Error loading additional tasks: {coreTasksError.message}
              </Box>
            ) : (
              // Combine existing tasks and new task form in one table
              <TaskTable
                tasks={
                  additionalTasks.length > 0
                    ? // If we have existing tasks, add them plus the empty form
                      [...additionalTasks]
                    : // If no existing tasks, just show the empty form
                      [newAdditionalTask]
                }
                headerType="additional"
                renderRow={(task) => (
                  <AdditionalTaskRow
                    key={task['id'] as string}
                    task={task}
                    onUpsertTask={(taskId, value) =>
                      handleUpsertTask(taskId, value, TaskType.AdditionalTask)
                    }
                    formMethods={masterFormMethods}
                    initializeTask={initializeTaskFormFields}
                    minDate={minDate}
                    isNotActive={isNotActive}
                  />
                )}
              />
            )}
          </CardSection>
          {/* Add New Task Button */}
          <Stack
            className="add-task-button"
            onClick={
              isNotActive
                ? undefined
                : () => {
                    // Create a new task record with a unique ID - all fields empty
                    const newId = `additional-task-${Date.now()}`;
                    const newTask = {
                      id: newId,
                      task: '',
                      assignee: '',
                      priority: '',
                      dueDate: '',
                      status: false,
                    };

                    // Add the new task to the existing list but keep the default task form at the end
                    setAdditionalTasks((prev) => [...prev, newTask]);
                  }
            }
            sx={{
              cursor: isNotActive ? 'default' : 'pointer',
              pointerEvents: isNotActive ? 'none' : 'auto',
              opacity: isNotActive ? 0.4 : 1,
              '& .add-task-icon': {
                color: isNotActive ? '#999' : 'inherit',
              },
            }}
          >
            <AddCircleOutlineIcon className="add-task-icon" /> ADD ANOTHER TASK
          </Stack>
        </ConvergeDetailAccordion>
      </Box>
      {/* Footer */}
    </ClaimEntityPageContainer>
  );
};

interface CardSectionProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * CardSection - A reusable component for grouping related fields
 */
const CardSection: React.FC<CardSectionProps> = ({ children, className }) => (
  <Box>{children}</Box>
);
